import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiGetAnswerCodes } from 'api/quote-marketplace';
import type { AnswerCodesData, AnswerCodeOption } from 'api/quote-marketplace';
import { isEffectiveDateWarningCode } from '../constants';
import type { EffectiveDateRange } from './useAnswerCodes';

export interface PropertyTypeOption {
  code: string;
  label: string;
}

interface UsePropertyTypeOptionsReturn {
  options: PropertyTypeOption[];
  dateRange: EffectiveDateRange | null;
  isOutdatedDate: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Risk types offered when starting a property quote from the marketplace.
 *
 * Same source and same query key as the Location(s) tab's Risk Type / Type
 * field, so picking a type here warms the cache the form then reuses.
 * Habitational answer codes are versioned by effective date, so the options are
 * only valid for the date they were fetched with — callers must refetch when
 * the broker changes it.
 */
export const usePropertyTypeOptions = (
  effectiveDate: string | null,
  enabled = true,
): UsePropertyTypeOptionsReturn => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['acturis', 'answer-codes', effectiveDate, 'habitational'],
    queryFn: () => apiGetAnswerCodes(effectiveDate, 'habitational'),
    enabled: enabled && effectiveDate !== null,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  }) as {
    data: AnswerCodesData | undefined;
    isLoading: boolean;
    error: Error | null;
  };

  const options = useMemo<PropertyTypeOption[]>(() => {
    const locationTypes = data?.location_type;
    if (!Array.isArray(locationTypes)) return [];
    return locationTypes.map((option: AnswerCodeOption) => ({
      code: option.code,
      label: option.label,
    }));
  }, [data]);

  const dateRange = useMemo<EffectiveDateRange | null>(() => {
    const range = data?.effective_date_range;
    if (!range?.min_date || !range?.max_date) return null;
    return {
      minDate: new Date(range.min_date + 'T00:00:00'),
      maxDate: new Date(range.max_date + 'T00:00:00'),
    };
  }, [data]);

  return {
    options,
    dateRange,
    isOutdatedDate: isEffectiveDateWarningCode(data?.warning?.code),
    isLoading,
    error,
  };
};
